// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import InjectCss from "@insite/mobius/utilities/injectCss";
import { color, xClassNames, xScreen } from "@synd/XDL/Utilities";
import styled, { css } from "styled-components";
import { SwiperProps } from "swiper/react";

const lazyloadSlides = css`
    &&& .swiper-lazy-preloader {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: ${color("common.black", 0.33)};
        border: 0;
        animation: unset;
        border: unset;
        border-radius: 2px;
        margin: 0;
        // for now lazy loaded it but don't show loader
        display: none;
        &::after {
            content: "";
            position: absolute;
            left: 50%;
            top: 50%;
            display: block;
            transform: translate(-50%, -50%);
            animation: swiper-preloader-spin 1s infinite linear;
            width: 40px;
            height: 40px;
            z-index: 10;
            border-radius: 50%;
            border: 4px solid var(--swiper-preloader-color, var(--swiper-theme-color));
            border-top-color: transparent;
        }
    }
`;

export const CarouselContainer = styled.div.attrs(props => ({
    className: xClassNames.append(["carousel-container", props.className]),
})) <{
    isAutoplay?: boolean;
    withShadow?: boolean;
    shadowWidth?: string;
    prevDisabled?: boolean;
    nextDisabled?: boolean;
    persistShadow?: boolean;
    shadowColor?: string;
    withNavigation?: SwiperProps["navigation"];
    direction?: SwiperProps["direction"];
    height?: SwiperProps["height"];
    slideMaxWidth?: string;
    slideWidth?: string;
    showArrowsOutside?: boolean;
    showOnMobile?: boolean;
    isVertical?: boolean;
}>`
    width: 100%;

    ${({ showArrowsOutside, showOnMobile, isVertical }) =>
        showArrowsOutside &&
        css`
            display: flex;
            align-items: center;
            gap: 16px;
            ${isVertical
                ? css`
                      flex-direction: column;
                      height: 100%;
                  `
                : css`
                      .swiper {
                          width: 100%;
                      }
                      ${showOnMobile &&
                    css`
                          & > div {
                              width: calc(100% - 80px);
                              margin: 0 auto;
                          }
                      `}
                  `}

            ${!showOnMobile &&
            css`
                ${xScreen("lte", "sm")} {
                    display: initial;
                    & > div {
                        width: 100%;
                        margin: 0 auto;
                    }
                }
            `}
        `}

    .swiper-slide {
        // to make all slides has the same height
        height: auto;
        // To fix swiper render issue
        margin-top: 1px;
        margin-bottom: 1px;
        position: relative;
        > div {
            max-width: 100%;
        }
        &.swiper-slide-visible:not(.swiper-slide-fully-visible) {
            opacity: 0.5;
        }
    }

    // hide OOTB swiper arrow navigation and use custom arrow navigation
    .swiper-button-prev,
    .swiper-button-next {
        display: none;
    }

    // override OOTB .swiper-pagination-bullets styles
    .swiper-pagination-bullets.swiper-pagination-horizontal {
        bottom: initial;
        top: initial;
        left: initial;
        width: initial;
    }

    ${({ isAutoplay, slideWidth }: any) =>
        isAutoplay &&
        css`
            .swiper-slide {
                width: ${slideWidth};
                height: unset;
            }
        `}

    ${lazyloadSlides}
    ${InjectCss}
`;

const shadowEl = css`
    content: "";
    position: absolute;
    display: block;
    height: 100%;
    width: ${({ shadowWidth }: any) => shadowWidth};
    background: ${({ shadowColor }: any) =>
        css`linear-gradient(90deg, ${color(shadowColor, 1)} 0%, ${color(shadowColor, 0)} 100%)`};
    top: 0;
    z-index: 420;
    pointer-events: none;
`;

const shadowElVertical = css`
    content: "";
    position: absolute;
    display: block;
    width: 100%;
    height: 30px;
    background: ${({ shadowColor }: any) =>
        css`linear-gradient(180deg, ${color(shadowColor, 1)} 0%, ${color(shadowColor, 0)} 100%)`};
    top: 16px;
    z-index: 420;
    pointer-events: none;
`;

export const coreSwiperShadow = css`
    ${({ withShadow }: any) => {
        if (withShadow) {
            return css`
                .swiper-horizontal::after {
                    ${shadowEl}
                    right: -1px;
                    transform: scaleX(-1);
                    ${({ nextDisabled, persistShadow }: any) => {
                    if (nextDisabled && !persistShadow) {
                        return css`
                                display: none;
                            `;
                    }
                }}
                }
                .swiper-horizontal::before {
                    ${shadowEl}
                    ${({ prevDisabled, persistShadow }: any) => {
                    if (prevDisabled && !persistShadow) {
                        return css`
                                display: none;
                            `;
                    }
                }}
                    left: -1px;
                }
                .swiper-vertical::after {
                    ${shadowElVertical}
                    top: unset;
                    bottom: 0;
                    transform: scaleY(-1);
                    ${({ nextDisabled, persistShadow }: any) => {
                    if (nextDisabled && !persistShadow) {
                        return css`
                                display: none;
                            `;
                    }
                }}
                }
                .swiper-vertical::before {
                    ${shadowElVertical}
                    ${({ prevDisabled, persistShadow }: any) => {
                    if (prevDisabled && !persistShadow) {
                        return css`
                                display: none;
                            `;
                    }
                }}
                }
            `;
        }
    }}
`;
